import React, { memo, useEffect, useMemo, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'

import { Combobox } from '../../../common/components/combobox'
import Input from '../../../common/components/Input'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import useKeyboardHandle from '../../../common/hooks/useKeyboardHandle'
import { CloseIcon } from '../../../common/icons/close-icon/CloseIcon'
import {
    ExternalServer,
    Partner,
    useExternalServerCreateMutation,
    useExternalServerGetPartnersQuery,
    useExternalServerUpdatePartnerConnectionsMutation,
    usePartnerGetAllAllQuery,
} from '../../../common/redux/api/web-api'
import BootstrapModalLayout from '../../../ui/BootstrapModalLayout'
import Text from '../../../ui/Text'
import { STableHead, STable, STableCell, STableRow, STableBody } from '../../../lib/logs-table/logs-table.styles'
import useText from '../../../common/hooks/useText'
import { Button } from '../../../common/components/Button'

export type ExternalServerCreateModalProps = {
    className?: ClassName
    style?: React.CSSProperties
    externalServerData?: ExternalServer[]
    externalServer: ExternalServer
    closeModal: () => void
}

type ExternalServerInputs = {
    url: string
    description: string
    initialAuthorizationKey?: string
}

type PartnerOptions = {
    value: string | number
    label: string
}

export const SWidget = styled.div`
    width: 100%;
    overflow: auto;
    max-height: 300px;
    scroll-behavior: smooth;
    &::-webkit-scrollbar {
        display: none;
    }
`

export const ModalList = styled.div`
    color: ${({ theme }) => theme.font.darkestColor};
    font-weight: 500;
    font-size: 15px;
    margin: 5px 0px 18px 0px;
`

export const ExternalServerCreateModal = memo(function ExternalServerCreateModal({
    className,
    closeModal,
    externalServerData,
    externalServer,
    style,
}: ExternalServerCreateModalProps): React.ReactElement {
    const text = useText()
    const [addedPartners, setAddedPartners] = useState<number[]>([])
    const [isPartnerListChanged, setIsPartnerListChanged] = useState<boolean>(false)
    const {
        register,
        getValues,
        formState: { errors, isValid },
    } = useForm<ExternalServerInputs>({ mode: 'all' })

    const isNameExists = (name: string) => {
        const nameExists = externalServerData?.some((item) => item?.description === name)
        return nameExists ? text('lblCpmsNameDuplicateError') : undefined
    }
    const isUrlExists = (url: string) => {
        const urlExists = externalServerData?.some((item) => item?.url === url)

        return urlExists ? text('lblCpmsUrlDuplicate') : undefined
    }

    const { data: selectedPartners, isLoading: loadingPartners } = useExternalServerGetPartnersQuery(
        {
            externalServerId: externalServer?.id,
        },
        {
            skip: !externalServer || !externalServer.id,
        }
    )
    const [createNewExternalServer] = useExternalServerCreateMutation()
    const [addPartner] = useExternalServerUpdatePartnerConnectionsMutation({})
    const { data: allPartners } = usePartnerGetAllAllQuery({ excludeOrphans: true })

    const partners = useMemo(() => {
        if (allPartners) return new Map(allPartners?.map((partner: Partner) => [partner.id, partner]))
    }, [allPartners])

    useEffect(() => {
        if (loadingPartners) return

        const preAddedPartners: number[] = selectedPartners?.map((obj) => obj.id) ?? []

        setAddedPartners(preAddedPartners)
    }, [selectedPartners])

    const doAddNewUrl = async () => {
        try {
            const response = await createNewExternalServer({
                externalServer: getValues(),
            }).unwrap()
            const serverId = response?.id
            if (serverId && addedPartners) {
                await addPartner({
                    externalServerId: serverId,
                    body: addedPartners,
                }).unwrap()
            }
            notifySuccess(text('lblServerCreated'))
            closeModal()
        } catch (e) {
            notifyError(text('lblServerCreateError'))
        }
    }

    const doEditPartner = async () => {
        try {
            if (externalServer?.id) {
                await addPartner({
                    externalServerId: externalServer?.id,
                    body: addedPartners,
                }).unwrap()
            }
            notifySuccess(text('lblPartnerAssigned'))
            closeModal()
        } catch (e) {
            notifyError(text('lblPartnerAssignFailed'))
        }
    }

    const partnerOptions = allPartners
        ? allPartners
              ?.filter((partner) => !addedPartners?.includes(partner?.id))
              .map((data) => ({
                  value: data?.id || '',
                  label: `${data?.name} - ${data?.email}` || '',
              }))
        : []

    const handleRemove = (valueToRemove: number | string) => {
        const tempPartners = addedPartners.filter((partner) => partner !== valueToRemove)
        setAddedPartners(tempPartners)
        setIsPartnerListChanged(true)
    }

    const handleAdd = (optionToAdd: number) => {
        const tempPartners = [...addedPartners, optionToAdd]
        setAddedPartners(tempPartners)
        setIsPartnerListChanged(true)
    }

    useKeyboardHandle({
        onEnterPress: () => {
            isPartnerListChanged && externalServer ? doEditPartner() : doAddNewUrl()
        },

        onEscapePress: closeModal,
    })

    const sortPartnerNames = (prevPartnerId: number, nextPartnerId: number) => {
        if (partners) {
            const prevPartnerName = partners.get(prevPartnerId)?.name ?? ''
            const nextPartnerName = partners.get(nextPartnerId)?.name ?? ''
            return prevPartnerName.localeCompare(nextPartnerName, undefined, {
                numeric: true,
                sensitivity: 'base',
            })
        }

        return 0
    }

    return (
        <BootstrapModalLayout
            data-testid="create-modal"
            className={cn(className)}
            style={style}
            title={
                externalServer ? text('lblEditExternalServerModalWidget') : text('lblCreateExternalServerModalWidget')
            }
            size={'lg'}
            actions={[
                {
                    btn: 'btn-secondary',
                    callback: closeModal,
                    label: text('btnCancel'),
                },
                {
                    callback: externalServer ? doEditPartner : doAddNewUrl,
                    disabled: externalServer ? !isPartnerListChanged : !isValid,
                    label: text('btnSave'),
                },
            ]}
        >
            <>
                <form data-testid="create-modal-form" autoComplete="off">
                    <Input
                        labelText={text('lblCpmsName')}
                        {...register('description', {
                            required: {
                                value: true,
                                message: text('lblCpmsNameRequired'),
                            },
                            maxLength: {
                                value: 50,
                                message: text('lblNameError'),
                            },
                            validate: {
                                nameExists: isNameExists,
                            },
                        })}
                        id="name"
                        defaultValue={externalServer ? externalServer?.description : ''}
                        error={externalServer ? '' : errors.description?.message}
                        subtitle={externalServer ? '' : text('lblCpmsNameInfoText')}
                        className="noBorder-input"
                        data-testid="name-input"
                        required
                        readonly={externalServer ? true : false}
                    />

                    {!externalServer && (
                        <div className="margin-top-med ">
                            <Input
                                labelText={text('lblCpmsUrl')}
                                id="cpms-url"
                                {...register('url', {
                                    required: {
                                        value: true,
                                        message: text('lblCpmsUrlRequired'),
                                    },

                                    pattern: {
                                        value: /^(ws|wss):\/\/[^\s/$.?#][^\s]*[^\s$.?#/](\/)$/i,
                                        message: text('lblCpmsUrlInvalidText'),
                                    },
                                    validate: {
                                        urlExists: isUrlExists,
                                    },
                                })}
                                error={errors?.url?.message}
                                subtitle={externalServer ? '' : text('lblCpmsUrlInfoText')}
                                className="noBorder-input"
                                autoComplete="off"
                                data-testid="url-input"
                                required
                            />
                        </div>
                    )}
                    {externalServer?.initialAuthorizationKey || !externalServer ? (
                        <>
                            <div className="margin-top-med ">
                                <Input
                                    id="cpms-auth-key"
                                    labelText={text('lblPassword')}
                                    {...register('initialAuthorizationKey')}
                                    defaultValue={externalServer ? externalServer?.initialAuthorizationKey : ''}
                                    className="noBorder-input password"
                                    data-testid="password-input"
                                    type="password"
                                    autoComplete="new-password"
                                    readonly={externalServer ? true : false}
                                />
                            </div>
                        </>
                    ) : (
                        <></>
                    )}
                    <div className="margin-top-med ">
                        <Combobox
                            id="select-partner"
                            labelText={text('lblSelectPartner')}
                            options={partnerOptions}
                            onChange={(option: PartnerOptions) => handleAdd(option.value)}
                            enableDefaultOption={false}
                        />
                    </div>
                </form>
            </>
            {addedPartners && addedPartners.length > 0 && (
                <div className="margin-top-med">
                    <SWidget>
                        <STable>
                            <thead>
                                <tr>
                                    <STableHead>
                                        <Text code="lblAssignedPartners" />
                                    </STableHead>
                                    <STableHead>
                                        <Text code="lblEmail" />
                                    </STableHead>
                                    <STableHead></STableHead>
                                </tr>
                            </thead>

                            <STableBody>
                                {addedPartners.sort(sortPartnerNames).map((key) => (
                                    <STableRow key={key}>
                                        <STableCell>{partners?.get(key)?.name}</STableCell>
                                        <STableCell>{partners?.get(key)?.email}</STableCell>
                                        <STableCell>
                                            <Button
                                                className="transparent-btn"
                                                onClick={() => {
                                                    handleRemove(key)
                                                }}
                                                data-testid="remove-button"
                                            >
                                                <CloseIcon />
                                            </Button>
                                        </STableCell>
                                    </STableRow>
                                ))}
                            </STableBody>
                        </STable>
                    </SWidget>
                </div>
            )}
        </BootstrapModalLayout>
    )
})
